import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { DialogProvider, Tabs, useLocalizeMessage, useToastNotification } from 'libs.nucleus';
import { AuthContext, ApiClientService, useApiClient } from 'libs.react_internal';
import { ConsentEditorModal } from '../components/consent_editor_modal.component';
import { NewConsentVersionModal } from '../components/new_consent_version_modal.component';
import { getFileNameToSiteIds } from '../consent_agent/consent_agent.types';
import { useConsentAgent } from '../consent_agent/use_consent_agent.hook';
import { useConsentContext } from '../hooks/use_consent_context.hook';
import { applyPendingTemplateAssignments, countAssignedSitesByTemplateKey, namesAssignedSitesByTemplateKey, } from '../mom_adapter/assignment_compute';
import { getAssignments, getEnvironments, getSites } from '../mom_adapter/getters';
import { setAssignmentsForDocument } from '../mom_adapter/setters';
import { classifyConsentUploadFile } from '../utils/consent_upload_file.helpers';
import { isDuplicateConsentTemplateNameError } from '../utils/library_error';
import { getUniqueFileName } from '../utils/unique_file_name.helpers';
import { ConsentManagerUI } from './consent_manager_ui/consent_manager_ui.component';
import { ConsentTemplateConfigurationView } from './consent_template_configuration_view/consent_template_configuration_view.component';
export const STUDY_METADATA_LOAD_ATTEMPTS = 3;
const STUDY_METADATA_RETRY_BASE_MS = 500;
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
export const ConsentView = forwardRef(function ConsentView({ studyId, files, onUpload, onEditRow, onFilesChange, canEditTemplate, canViewTemplateSource, canArchiveTemplate, canDeleteTemplate, canCreateTemplate, isDocGenEconsentEnabled = false, header, }, ref) {
    const translate = useLocalizeMessage();
    const { addNotification } = useToastNotification();
    const { entityId } = useContext(AuthContext);
    const momClient = useApiClient(ApiClientService.MOM);
    const momClientRef = useRef(momClient);
    momClientRef.current = momClient;
    const [sites, setSites] = useState(undefined);
    const [environments, setEnvironments] = useState(undefined);
    const [studyMetadataStatus, setStudyMetadataStatus] = useState('loading');
    const [studyMetadataReloadCount, setStudyMetadataReloadCount] = useState(0);
    const { processFiles } = useConsentAgent(sites);
    const { state, consentFiles, isLoading, templateTotal, loadDocuments, loadAssignmentCatalog, hydrateDocument, uploadConsentFile, archiveTemplateVersion, cloneTemplateVersion, deleteTemplateVersion, getArchiveImpact, setActiveDocument, activeDocument, fetchDocumentPdf, } = useConsentContext();
    const [templatesLoadFailed, setTemplatesLoadFailed] = useState(false);
    const [templatesReloadCount, setTemplatesReloadCount] = useState(0);
    const appliedTemplatesReloadCount = useRef(0);
    const [templatePage, setTemplatePage] = useState(0);
    const [listQuery, setListQuery] = useState({ search: '', status: 'all' });
    const [assignedSitesByTemplateKey, setAssignedSitesByTemplateKey] = useState(null);
    const [assignmentCountsReloadCount, setAssignmentCountsReloadCount] = useState(0);
    const invalidateAssignedSiteCounts = useCallback(() => {
        // Unknown, not zero, while the study-wide count is refetched — a write must not flash
        // incomplete Published on rows that were assigned a moment ago.
        setAssignedSitesByTemplateKey(null);
        setAssignmentCountsReloadCount((count) => count + 1);
    }, []);
    useImperativeHandle(ref, () => ({
        removeFile: (id) => {
            if (!canArchiveTemplate) {
                return;
            }
            void archiveTemplateVersion(id);
        },
    }), [archiveTemplateVersion, canArchiveTemplate]);
    const consentFileList = useMemo(() => consentFiles.map((f) => ({ id: f.id, title: f.title || '' })), [consentFiles]);
    const consentTemplateListItems = useMemo(() => consentFiles.map((f) => ({
        id: f.id,
        title: f.title || '',
        status: f.status,
        templateKey: f.templateKey,
        version: f.version,
    })), [consentFiles]);
    const onFilesChangeRef = useRef(onFilesChange);
    onFilesChangeRef.current = onFilesChange;
    useEffect(() => {
        onFilesChangeRef.current?.(consentFileList);
    }, [consentFileList]);
    useEffect(() => {
        if (!entityId || !studyId) {
            return;
        }
        let cancelled = false;
        async function fetchStudySitesAndEnvironments() {
            for (let attempt = 1; !cancelled; attempt++) {
                try {
                    const [fetchedSites, fetchedEnvironments] = await Promise.all([
                        getSites(momClientRef.current, entityId, studyId),
                        getEnvironments(momClientRef.current, entityId, studyId),
                    ]);
                    if (!cancelled) {
                        setSites(fetchedSites);
                        setEnvironments(fetchedEnvironments);
                        setStudyMetadataStatus('loaded');
                    }
                    return;
                }
                catch {
                    if (attempt >= STUDY_METADATA_LOAD_ATTEMPTS) {
                        // Leaving both undefined is not neutral: the environment selector reads `?? []` and
                        // tells the author the study has no environments, and the sites table spins forever.
                        // Neither is something we read, so the failure has to be shown as a failure.
                        if (!cancelled) {
                            setStudyMetadataStatus('error');
                        }
                        return;
                    }
                    await delay(STUDY_METADATA_RETRY_BASE_MS * attempt);
                }
            }
        }
        setStudyMetadataStatus('loading');
        void fetchStudySitesAndEnvironments();
        return () => {
            cancelled = true;
        };
    }, [entityId, studyId, studyMetadataReloadCount]);
    const consentFilesRef = useRef(consentFiles);
    consentFilesRef.current = consentFiles;
    const consentFileKeyFingerprint = useMemo(() => consentFiles
        .map((f) => `${f.id}:${f.templateKey}`)
        .sort()
        .join('|'), [consentFiles]);
    useEffect(() => {
        let cancelled = false;
        const refresh = templatesReloadCount !== appliedTemplatesReloadCount.current;
        appliedTemplatesReloadCount.current = templatesReloadCount;
        void loadDocuments(templatePage + 1, { ...listQuery, refresh }).then(() => {
            if (!cancelled) {
                setTemplatesLoadFailed(false);
            }
        }, () => {
            if (!cancelled) {
                setTemplatesLoadFailed(true);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [loadDocuments, templatesReloadCount, templatePage, listQuery]);
    useEffect(() => {
        if (!entityId || !studyId || environments === undefined || sites === undefined) {
            setAssignedSitesByTemplateKey(null);
            return;
        }
        const environmentList = environments;
        const siteList = sites;
        const workspaceEntityId = entityId;
        const workspaceStudyId = studyId;
        let cancelled = false;
        async function loadAssignedSiteCounts() {
            try {
                const catalog = await loadAssignmentCatalog();
                const perEnvironment = await Promise.all(environmentList.map((environment) => getAssignments(momClientRef.current, workspaceEntityId, workspaceStudyId, environment.id, catalog)));
                const siteIdByEnvironmentSiteId = new Map(siteList.map((site) => [site.id, site.siteId]));
                const labelByEnvironmentSiteId = new Map(siteList.map((site) => [site.id, site.name || site.number || site.key || String(site.siteId)]));
                const counts = countAssignedSitesByTemplateKey(perEnvironment, catalog, siteIdByEnvironmentSiteId);
                const names = namesAssignedSitesByTemplateKey(perEnvironment, catalog, siteIdByEnvironmentSiteId, labelByEnvironmentSiteId);
                if (!cancelled) {
                    setAssignedSitesByTemplateKey({ counts, names });
                }
            }
            catch {
                // Unknown is not zero: leave the map unset so Published rows do not flash incomplete.
                if (!cancelled) {
                    setAssignedSitesByTemplateKey(null);
                }
            }
        }
        void loadAssignedSiteCounts();
        return () => {
            cancelled = true;
        };
    }, [
        entityId,
        studyId,
        environments,
        sites,
        consentFileKeyFingerprint,
        assignmentCountsReloadCount,
        loadAssignmentCatalog,
    ]);
    const filesForTable = useMemo(() => consentFiles.map((file) => ({
        ...file,
        assignedSiteCount: assignedSitesByTemplateKey?.counts.get(file.templateKey),
        assignedSiteNames: assignedSitesByTemplateKey?.names.get(file.templateKey),
    })), [consentFiles, assignedSitesByTemplateKey]);
    useEffect(() => {
        if (files) {
            files.forEach((f) => {
                const existing = consentFiles.find((p) => p.id === f.id);
                if (!existing) {
                    void uploadConsentFile(new File([], f.fileName || f.title || ''), f.fileName || f.title || '', {
                        sites: f.site,
                    });
                }
            });
        }
    }, [files, consentFiles, uploadConsentFile]);
    const [isEditing, setIsEditing] = useState(false);
    // Only a template uploaded in this session opens on the setup steps; reopening it from the list
    // lands on the document like any other draft.
    const [newTemplateId, setNewTemplateId] = useState(null);
    const [newVersionTemplateId, setNewVersionTemplateId] = useState(null);
    const [configurationViewDocumentId, setConfigurationViewDocumentId] = useState(null);
    const handleEditRow = useCallback(async (id, source) => {
        const listRow = consentFilesRef.current.find((file) => file.id === id);
        const openConfiguration = () => {
            setIsEditing(false);
            setConfigurationViewDocumentId(id);
        };
        if (listRow && listRow.status !== 'Draft') {
            openConfiguration();
            return;
        }
        const hydrated = await hydrateDocument(id);
        const status = listRow?.status ?? hydrated?.status;
        if (status && status !== 'Draft') {
            openConfiguration();
            return;
        }
        const existingDoc = state.documents.find((d) => d.id === id);
        const pdfDataUrl = source?.pdfDataUrl || existingDoc?.pdfDataUrl;
        const assetId = source?.assetId || existingDoc?.assetId;
        if (!pdfDataUrl) {
            const fetchedPdfDataUrl = await (assetId ? fetchDocumentPdf(id, assetId) : fetchDocumentPdf(id));
            // A document that has an asset but no readable PDF would open the editor on react-pdf's
            // own untranslated "No PDF file specified." with nothing to place elements on. Stay on the
            // list and say so — the row's edit action is itself the retry. Documents with no asset at
            // all (non-PDF rows) still open as before.
            if (assetId && !fetchedPdfDataUrl) {
                addNotification({
                    type: 'error',
                    title: translate('Could not open the consent document'),
                    subtitle: translate('The PDF could not be loaded. Open the template from the list to try again.'),
                });
                return;
            }
        }
        setActiveDocument(id);
        setIsEditing(true);
        if (onEditRow) {
            onEditRow(new File([], ''), id);
        }
    }, [addNotification, hydrateDocument, state.documents, fetchDocumentPdf, setActiveDocument, onEditRow, translate]);
    const handleRetryStudyMetadata = useCallback(() => {
        setStudyMetadataReloadCount((count) => count + 1);
    }, []);
    const handleRetryTemplates = useCallback(() => {
        setTemplatesLoadFailed(false);
        setTemplatePage(0);
        setTemplatesReloadCount((count) => count + 1);
    }, []);
    const handleListQueryChange = useCallback((query) => {
        setListQuery((current) => current.search === query.search && (current.status ?? 'all') === query.status
            ? current
            : { search: query.search, status: query.status });
    }, []);
    useEffect(() => {
        setTemplatePage(0);
    }, [listQuery.search, listQuery.status]);
    const handleCloseModal = useCallback(() => {
        setIsEditing(false);
        setNewTemplateId(null);
        setActiveDocument(null);
    }, [setActiveDocument]);
    // The publish dialog needs the lineage's site count across every environment, not only the
    // environment the wizard currently has open. Draft checks are not in MOM yet, so a saved-only
    // count would tell the builder that nobody is assigned after they just checked a site.
    const loadAssignedSiteCount = useCallback(async (documentId, pendingByEnv) => {
        if (!entityId || !studyId || environments === undefined || sites === undefined) {
            throw new Error('Study sites and environments are not loaded yet');
        }
        const catalog = await loadAssignmentCatalog(true);
        const templateKey = catalog.find((file) => file.id === documentId)?.templateKey;
        if (!templateKey) {
            throw new Error('Consent template is not in the assignment catalog');
        }
        const perEnvironment = await Promise.all(environments.map((environment) => getAssignments(momClientRef.current, entityId, studyId, environment.id, catalog)));
        const withDraft = applyPendingTemplateAssignments(perEnvironment, environments.map((environment) => environment.id), templateKey, pendingByEnv);
        const siteIdByEnvironmentSiteId = new Map(sites.map((site) => [site.id, site.siteId]));
        const counts = countAssignedSitesByTemplateKey(withDraft, catalog, siteIdByEnvironmentSiteId);
        return counts.get(templateKey) ?? 0;
    }, [entityId, studyId, environments, sites, loadAssignmentCatalog]);
    const loadTemplateSiteAssignments = useCallback(async (environmentId) => {
        if (!entityId || !studyId) {
            return [];
        }
        const catalog = await loadAssignmentCatalog();
        return getAssignments(momClientRef.current, entityId, studyId, environmentId, catalog);
    }, [entityId, studyId, loadAssignmentCatalog]);
    const saveTemplateSiteAssignments = useCallback(async (environmentId, templateKey, selectedEnvironmentSiteIds, environmentSites) => {
        if (!entityId || !studyId) {
            throw new Error('Study scope is required to save site assignments');
        }
        const catalog = await loadAssignmentCatalog(true);
        if (!catalog.some((file) => file.templateKey === templateKey)) {
            throw new Error('Consent template is not in the assignment catalog');
        }
        const current = await getAssignments(momClientRef.current, entityId, studyId, environmentId, catalog);
        const next = await setAssignmentsForDocument(momClientRef.current, entityId, studyId, environmentId, current, templateKey, selectedEnvironmentSiteIds, environmentSites, catalog);
        invalidateAssignedSiteCounts();
        return next;
    }, [entityId, studyId, invalidateAssignedSiteCounts, loadAssignmentCatalog]);
    const handleFilesUploaded = useCallback(async (uploadedFiles) => {
        if (uploadedFiles.length === 0) {
            return;
        }
        const classified = uploadedFiles.map((file) => ({ file, kind: classifyConsentUploadFile(file) }));
        const hasSpreadsheets = classified.some(({ kind }) => kind === 'spreadsheet');
        const hasTemplateFiles = classified.some(({ kind }) => kind === 'pdf');
        for (const { file, kind } of classified) {
            if (kind === 'unsupported') {
                addNotification({
                    type: 'error',
                    title: translate('{fileName} is not a supported file type', { fileName: file.name }),
                    subtitle: translate('Upload a PDF consent document, or a spreadsheet for site mapping.'),
                });
            }
            else if (kind === 'docx') {
                addNotification({
                    type: 'error',
                    title: translate('{fileName} is not yet supported', { fileName: file.name }),
                    subtitle: translate('Upload a PDF. Word documents cannot be used as a source ICF yet.'),
                });
            }
            else if (kind === 'spreadsheet' && !hasTemplateFiles) {
                addNotification({
                    type: 'error',
                    title: translate('{fileName} cannot be uploaded on its own', { fileName: file.name }),
                    subtitle: translate('A spreadsheet is for site mapping. Upload it together with a PDF consent document.'),
                });
            }
        }
        if (!hasTemplateFiles) {
            return;
        }
        const mappingBatch = classified
            .filter(({ kind }) => kind === 'pdf' || kind === 'spreadsheet')
            .map(({ file }) => file);
        let fileNameToSiteIds = getFileNameToSiteIds(undefined);
        if (hasSpreadsheets) {
            try {
                fileNameToSiteIds = getFileNameToSiteIds(await processFiles(mappingBatch));
            }
            catch {
                for (const { file, kind } of classified) {
                    if (kind !== 'spreadsheet') {
                        continue;
                    }
                    addNotification({
                        type: 'error',
                        title: translate('{fileName} could not be used for site mapping', { fileName: file.name }),
                        subtitle: translate('Draft consent templates will still be created. Assign sites from the list.'),
                    });
                }
            }
        }
        // Track names already in use (existing consent files + names assigned earlier in this
        // same batch, since React state won't update mid-loop) so a repeated PDF name gets a macOS
        // style " (n)" suffix instead of silently merging into another template's version history.
        // Archived documents are excluded: they are dropped server-side by loadResources' `active`
        // filter but remain in the in-memory list (with status 'Archived') until reload, so an
        // archived name must not block reuse within the same session.
        const takenNamesLower = new Set(consentFilesRef.current.filter((file) => file.status !== 'Archived').map((file) => file.title.toLowerCase()));
        let lastCreated;
        for (const { file, kind } of classified) {
            if (kind !== 'pdf') {
                continue;
            }
            if (onUpload) {
                onUpload(file);
            }
            const sites = fileNameToSiteIds.get(file.name) ?? '—';
            const name = getUniqueFileName(file.name, takenNamesLower);
            takenNamesLower.add(name.toLowerCase());
            try {
                lastCreated = await uploadConsentFile(file, name, { sites });
                addNotification({
                    type: 'success',
                    title: translate('{fileName} was accepted', { fileName: name }),
                    subtitle: translate('A draft consent template was created.'),
                });
            }
            catch (error) {
                const title = translate('{fileName} could not be uploaded', { fileName: name });
                if (isDuplicateConsentTemplateNameError(error)) {
                    addNotification({
                        type: 'error',
                        title,
                        subtitle: translate('That title is already in use by another consent form'),
                    });
                }
                else {
                    addNotification({
                        type: 'error',
                        title,
                        subtitle: translate('Try again from the list.'),
                    });
                }
            }
        }
        if (lastCreated) {
            setNewTemplateId(lastCreated.id);
            await handleEditRow(lastCreated.id, {
                assetId: lastCreated.assetId,
                pdfDataUrl: lastCreated.pdfDataUrl,
            });
        }
    }, [addNotification, handleEditRow, onUpload, processFiles, translate, uploadConsentFile]);
    const tabItems = useMemo(() => [
        {
            label: translate('Upload files'),
            content: (_jsx(ConsentManagerUI, { files: filesForTable, onFilesUploaded: handleFilesUploaded, onEditRow: handleEditRow, onViewConfiguration: setConfigurationViewDocumentId, archiveTemplateVersion: archiveTemplateVersion, onNewVersionRequest: canCreateTemplate ? setNewVersionTemplateId : undefined, deleteTemplateVersion: deleteTemplateVersion, canEditTemplate: canEditTemplate, canViewTemplateSource: canViewTemplateSource, canArchiveTemplate: canArchiveTemplate, canDeleteTemplate: canDeleteTemplate, canCreateTemplate: canCreateTemplate, isDocGenEconsentEnabled: isDocGenEconsentEnabled, getArchiveImpact: getArchiveImpact, isLoading: isLoading, hasLoadError: templatesLoadFailed, onRetryLoad: handleRetryTemplates, templateTotal: templateTotal, onPageIndexChange: setTemplatePage, onListQueryChange: handleListQueryChange })),
        },
    ], [
        consentFiles,
        filesForTable,
        isLoading,
        templatesLoadFailed,
        templateTotal,
        handleRetryTemplates,
        handleListQueryChange,
        handleFilesUploaded,
        handleEditRow,
        archiveTemplateVersion,
        deleteTemplateVersion,
        canEditTemplate,
        canViewTemplateSource,
        canArchiveTemplate,
        canDeleteTemplate,
        canCreateTemplate,
        isDocGenEconsentEnabled,
        getArchiveImpact,
    ]);
    return (_jsx(DialogProvider, { children: _jsxs("div", { className: 'flex min-h-0 w-full flex-1 flex-col', children: [!isEditing && header, configurationViewDocumentId === null && !isEditing && _jsx(Tabs, { tabItems: tabItems, defaultIndex: 0 }), _jsx(ConsentTemplateConfigurationView, { isOpen: configurationViewDocumentId !== null, documentId: configurationViewDocumentId, onClose: () => setConfigurationViewDocumentId(null), canEditTemplate: canEditTemplate, entityId: entityId, studyId: studyId, environments: environments, sites: sites, onAssignmentsSaved: invalidateAssignedSiteCounts }), _jsx(ConsentEditorModal, { isOpen: isEditing, document: activeDocument, consentFiles: consentTemplateListItems, loadAssignedSiteCount: loadAssignedSiteCount, onClose: handleCloseModal, canEditTemplate: canEditTemplate, isDocGenEconsentEnabled: isDocGenEconsentEnabled, isNewTemplate: activeDocument?.id === newTemplateId, siteAssignment: {
                        environments,
                        sites,
                        hasLoadError: studyMetadataStatus === 'error',
                        onRetryLoad: handleRetryStudyMetadata,
                        loadAssignments: loadTemplateSiteAssignments,
                        saveAssignments: saveTemplateSiteAssignments,
                    } }), canCreateTemplate ? (_jsx(NewConsentVersionModal, { isOpen: newVersionTemplateId !== null, templateId: newVersionTemplateId, sourceFile: consentFiles.find((file) => file.id === newVersionTemplateId) ?? null, sourceDocument: state.documents.find((document) => document.id === newVersionTemplateId) ?? null, loadSourceDocument: hydrateDocument, cloneTemplateVersion: cloneTemplateVersion, onClose: () => setNewVersionTemplateId(null), onVersionCreated: (newDocumentId) => {
                        setNewVersionTemplateId(null);
                        void handleEditRow(newDocumentId);
                    } })) : null] }) }));
});
